import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, Sparkles, FileText, RotateCcw } from "lucide-react";
import api from "@/api/client";
import AIStarfield from "@/components/AIStarfield";

const T = {
  en: {
    title: "Ask the docs",
    subtitle: "Powered by AI. Answers come only from this help center.",
    placeholder: "Ask anything about this product…",
    thinking: "Thinking…",
    sources: "Sources",
    suggestions: "Try asking:",
    error: "Something went wrong. Please try again in a moment.",
    reset: "New chat",
    welcome: "Hi! 👋 I'm your docs assistant. Ask me anything about this product and I'll answer using only the articles in this help center.",
  },
  ar: {
    title: "اسأل الدلائل",
    subtitle: "مدعوم بالذكاء الاصطناعي. الإجابات من هذا المركز فقط.",
    placeholder: "اطرح أي سؤال عن هذا المنتج…",
    thinking: "جارٍ التفكير…",
    sources: "المصادر",
    suggestions: "جرب أن تسأل:",
    error: "حدث خطأ. حاول مرة أخرى بعد لحظات.",
    reset: "محادثة جديدة",
    welcome: "مرحباً 👋 أنا مساعدك الذكي. اسألني أي سؤال عن هذا المنتج وسأجيب من مقالات هذا المركز فقط.",
  },
};

const SUGGESTIONS = {
  en: ["What is this product?", "How do I get started?", "What features are available?"],
  ar: ["ما هو هذا المنتج؟", "كيف أبدأ؟", "ما هي الميزات المتاحة؟"],
};

// Tiny markdown-ish renderer: bold, italic, line breaks, lists
function renderMarkdown(text) {
  if (!text) return null;
  // Escape HTML
  const safe = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>");
  // bullet lists
  const withLists = safe.replace(/(?:^|\n)([-*•] .+(?:\n[-*•] .+)*)/g, (m, block) => {
    const items = block.split(/\n/).map((l) => l.replace(/^[-*•]\s+/, "").trim());
    return "\n<ul>" + items.map((i) => `<li>${i}</li>`).join("") + "</ul>";
  });
  return <div dangerouslySetInnerHTML={{ __html: withLists.replace(/\n/g, "<br/>") }} />;
}

export default function AskAIWidget({ open, onClose, spaceSlug, lang = "en", primary = "#41B5E6", theme = "dark" }) {
  const t = T[lang] || T.en;
  const sugs = SUGGESTIONS[lang] || SUGGESTIONS.en;

  const [messages, setMessages] = useState(() => [{ role: "assistant", content: t.welcome }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Reset welcome when language changes
    setMessages([{ role: "assistant", content: t.welcome }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      // Hide the legacy Handle AI iframe so our panel is the only visible chat
      const iframe = document.querySelector('iframe[title="Handle AI chat"]');
      if (iframe) iframe.style.display = "none";
    } else {
      const iframe = document.querySelector('iframe[title="Handle AI chat"]');
      if (iframe) iframe.style.display = "";
    }
  }, [open]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    try {
      const history = messages
        .filter((m) => m.content !== t.welcome)
        .map((m) => ({ role: m.role, content: m.content }));
      const { data } = await api.post(
        `/public/ai-chat?space=${encodeURIComponent(spaceSlug)}`,
        { question: q, history, lang, space: spaceSlug }
      );
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.answer, sources: data.sources || [] },
      ]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: t.error, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMessages([{ role: "assistant", content: t.welcome }]);
    setInput("");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 flex items-stretch justify-end pointer-events-none"
      style={{ zIndex: 99999 }}
      data-testid="askai-widget-root"
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      {/* Scrim */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto animate-fadeIn"
        onClick={onClose}
      />

      {/* Ambient starfield drifting across the backdrop while panel is open */}
      <AIStarfield active={open} mode="ambient" color={primary} density={1} zIndex={1} />

      {/* Panel */}
      <aside
        className={`relative pointer-events-auto w-full max-w-md sm:max-w-lg h-full shadow-2xl flex flex-col askai-slide-in ${
          theme === "light" ? "bg-white text-zinc-900" : "bg-[#0a0a0b] text-white"
        }`}
        data-testid="askai-widget-panel"
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 p-4 border-b"
          style={{
            borderColor: theme === "light" ? "#e5e7eb" : "#1f1f23",
            background: `linear-gradient(135deg, ${primary}11, transparent)`,
          }}
        >
          <span
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `linear-gradient(135deg, ${primary}, ${primary}aa)`, boxShadow: `0 0 22px ${primary}66` }}
          >
            <Sparkles className="w-5 h-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base tracking-wider">{t.title}</div>
            <div className="text-[11px] opacity-60 mt-0.5">{t.subtitle}</div>
          </div>
          <button
            onClick={reset}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10"
            title={t.reset}
            data-testid="askai-reset"
          >
            <RotateCcw className="w-4 h-4 opacity-70" />
          </button>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10"
            data-testid="askai-close"
            aria-label="Close"
          >
            <X className="w-4 h-4 opacity-80" />
          </button>
        </div>

        {/* Messages */}
        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="askai-messages">
          {messages.map((m, i) => (
            <Message key={i} m={m} primary={primary} theme={theme} t={t} slug={spaceSlug} lang={lang} />
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-sm opacity-60" data-testid="askai-thinking">
              <span className="askai-dots">
                <span></span><span></span><span></span>
              </span>
              <span>{t.thinking}</span>
            </div>
          )}
        </div>

        {/* Suggestions */}
        {messages.length <= 1 && !busy && (
          <div className="px-4 pb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-50 mb-2 font-bold">{t.suggestions}</div>
            <div className="flex flex-wrap gap-1.5">
              {sugs.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid={`askai-suggestion-${s.slice(0, 12)}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    theme === "light"
                      ? "border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50"
                      : "border-zinc-800 hover:border-zinc-500 hover:bg-zinc-900"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className={`p-3 border-t flex items-end gap-2 ${theme === "light" ? "border-zinc-200" : "border-zinc-800"}`}
        >
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={t.placeholder}
            data-testid="askai-input"
            disabled={busy}
            className={`flex-1 resize-none text-sm px-3 py-2.5 rounded-2xl outline-none focus:ring-2 transition-all ${
              theme === "light"
                ? "bg-zinc-100 focus:ring-zinc-300"
                : "bg-zinc-900 focus:ring-zinc-700"
            }`}
            style={{ maxHeight: "120px" }}
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            data-testid="askai-send"
            className="w-10 h-10 flex items-center justify-center rounded-full transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
            style={{ background: `linear-gradient(135deg, ${primary}, ${primary}cc)`, boxShadow: `0 4px 16px ${primary}55` }}
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </form>
      </aside>
    </div>
  );
}

function Message({ m, primary, theme, t, slug, lang }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end" data-testid="askai-msg-user">
        <div
          className="max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed text-white"
          style={{ background: `linear-gradient(135deg, ${primary}, ${primary}cc)` }}
        >
          {m.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-2 max-w-[92%]" data-testid="askai-msg-assistant">
      <div
        className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed prose-doc-mini ${
          theme === "light" ? "bg-zinc-100 text-zinc-900" : "bg-zinc-900 text-zinc-100"
        } ${m.error ? "border border-red-500/40" : ""}`}
      >
        {renderMarkdown(m.content)}
      </div>
      {m.sources && m.sources.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          <span className="text-[9px] uppercase tracking-[0.2em] opacity-50 font-bold self-center">{t.sources}</span>
          {m.sources.slice(0, 4).map((s) => (
            <Link
              key={s.article_id}
              to={`/s/${slug}/articles/${s.article_slug}${lang === "ar" ? "?lang=ar" : ""}`}
              data-testid={`askai-source-${s.article_slug}`}
              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full transition-colors ${
                theme === "light"
                  ? "bg-white border border-zinc-200 hover:border-zinc-900 text-zinc-700"
                  : "bg-black border border-zinc-700 hover:border-zinc-400 text-zinc-300"
              }`}
              title={s.title}
            >
              <FileText className="w-2.5 h-2.5" />
              <span className="truncate max-w-[150px]">{s.title}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
